import React, { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import toast, { Toaster } from 'react-hot-toast';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../app/hooks';
import { RootState } from '../../app/store'; // Adjust the path to your store
import HistoryRouting from '../../components/HistoryRouting';
import RequiredAsterisk from '../../components/RequiredAsterisk';
import Button from '../../components/button/Button';
import Spinner from '../../components/spinner/Spinner';
import { setUserDetails } from '../../features/authSlice';
import {
  UpdateBrandInfo,
  UpdatePartnerInfo,
} from '../../features/partnerSlice';
import { extractFileName } from '../../components/branding/BrandingInformation';
import { FileUpload } from '../../components/FileUpload/fileUpload';

interface CompanyData {
  companyName: string;
  addressOne: string;
  addressTwo: string;
  city: string;
  state: string;
  zipcode: string;
  country: string;
  contactEmail: string;
  companyPhone: string;
  salesAgentId: string;
  id: string;
  websiteLink: string;
  facebookLink: string;
  instagramLink: string;
  other: string;
  tmCertificate: FileList;
  einNumber: string;
  additionalDocument: FileList;
}

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phonePattern =
  /^\+?[0-9]{1,4}[\s-]?\(?[0-9]{1,5}\)?[\s-]?[0-9]{1,9}[\s-]?[0-9]{1,9}$/;
const salesIdPattern = /^[0-9]{5}$/;
interface ModalProps {
  isLoading: boolean;
  children: React.ReactNode;
}

const Modal: React.FC<ModalProps> = ({ children, isLoading }) => {
  if (!isLoading) return null;

  return (
    <div className='fixed inset-0 bg-gray-900 bg-opacity-50 flex justify-center items-center z-50'>
      <div
        className={`bg-[#fff0] p-8 rounded-lg  relative w-[full] md:w-[27rem] px-4 md:px-8 py-12 text-center m-4 flex justify-center`}
      >
        <p className='text-base text-gray-700'>{children}</p>
      </div>
    </div>
  );
};

const CompanyInfo = () => {
  const { userDetails } = useSelector((state: RootState) => state.auth);
  const BrandInfoState = useSelector(
    (state: RootState) => state.partner.brandInfo
  );
  const loadingBrandInfo = useSelector(
    (state: RootState) => state.partner.loadingStates['GetBrandInfoState']
  );
  const dispatch = useAppDispatch();
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingSecond, setIsEditingSecond] = useState(false);
  const [isEditingThird, setIsEditingThird] = useState(false);
  const isLoading = useSelector((state: RootState) => state.partner.loading);
  const userId: any = userDetails?.id;
  const partnerId: any = userDetails?.partner?.id;
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<CompanyData>();

  const onSubmit: SubmitHandler<CompanyData> = async data => {
    const fieldMap: Record<string, string> = {
      companyName: 'name',
      addressOne: 'address_1',
      addressTwo: 'address_2',
      city: 'city',
      state: 'state',
      zipcode: 'zipcode',
      country: 'country',
      contactEmail: 'email',
      companyPhone: 'phone',
      salesAgentId: 'salesAgentId',
    };
    const companyData: (keyof CompanyData)[] = [
      'companyName',
      'addressOne',
      'addressTwo',
      'city',
      'state',
      'zipcode',
      'country',
      'contactEmail',
      'companyPhone',
      'salesAgentId',
    ];
    const brandingData = [
      'websiteLink',
      'facebookLink',
      'instagramLink',
      'other',
      'einNumber',
      'tmCertificate',
      'additionalDocument',
    ];

    try {
      const companyFormData = new FormData();
      const brandingFormData = new FormData();
      companyFormData.append('id', partnerId as string);
      brandingFormData.append('partnerId', partnerId as string);

      // تعبئة companyFormData فقط بالحقل المتغير
      companyData.forEach(field => {
        const backendField = fieldMap[field] || field;
        const newValue: any = data[field] ?? '';
        const oldValue: any = userDetails?.partner?.[backendField] ?? '';
        if (newValue !== oldValue) {
          companyFormData.append(field, newValue);
        }
      });

      // تعبئة brandingFormData فقط بالحقل المتغير
      brandingData.forEach(field => {
        const newValue: any = data[field as keyof typeof data];
        const oldValue: any =
          BrandInfoState?.[field as keyof typeof BrandInfoState];
        const isChanged =
          newValue instanceof File || newValue instanceof FileList
            ? true
            : newValue !== oldValue;

        if (isChanged) {
          if (newValue instanceof FileList) {
            Array.from(newValue).forEach(file =>
              brandingFormData.append(field, file)
            );
          } else if (newValue instanceof File) {
            brandingFormData.append(field, newValue);
          } else {
            brandingFormData.append(field, String(newValue ?? ''));
          }
        }
      });

      // فحص إذا الفورمين فارغين
      const isCompanyFormEmpty = Array.from(companyFormData.entries()).every(
        ([key]) => key === 'id'
      );
      const isBrandingFormEmpty = Array.from(brandingFormData.entries()).every(
        ([key]) => key === 'partnerId'
      );

      if (isCompanyFormEmpty && isBrandingFormEmpty) {
        toast.error('No changes to save');
        return;
      }

      // إرسال الأبديت
      let companyInfo: any;
      if (!isCompanyFormEmpty) {
        companyInfo = await dispatch(
          UpdatePartnerInfo(userId, companyFormData)
        );
      }
      let brandingInfo: any;
      if (!isBrandingFormEmpty) {
        brandingInfo = await dispatch(UpdateBrandInfo(brandingFormData));
      }

      // إظهار نتيجة الأبديت
      if (companyInfo?.status === 200 || brandingInfo?.status === 200) {
        toast.success('Company information updated successfully');
        setIsEditing(false);
        setIsEditingSecond(false);
        setIsEditingThird(false);
      }
    } catch (error) {
      console.error('Error updating company information:', error);
      toast.error('Failed to update company information. Please try again.');
    }
  };

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
  };

  const handleSecondEditToggle = () => {
    setIsEditingSecond(!isEditingSecond);
  };

  const handleThirdEditToggle = () => {
    setIsEditingThird(!isEditingThird);
  };

  useEffect(() => {
    const localUserData: any = localStorage.getItem('userDetails');
    if (localUserData) {
      const userDetailsss = JSON.parse(localUserData);
      setUserDetails(userDetailsss);
      dispatch(setUserDetails(userDetailsss));
    }
  }, [dispatch]);

  useEffect(() => {
    if (userDetails?.partner) {
      setValue('companyName', userDetails.partner.name);
      setValue('addressOne', userDetails.partner.address_1);
      setValue('addressTwo', userDetails.partner.address_2);
      setValue('city', userDetails.partner.city);
      setValue('state', userDetails.partner.state);
      setValue('zipcode', userDetails.partner.zipcode);
      setValue('country', userDetails.partner.country);
      setValue('contactEmail', userDetails.partner.email);
      setValue('companyPhone', userDetails.partner.phone);
      setValue('salesAgentId', userDetails.partner.salesAgentId);
      setValue('websiteLink', BrandInfoState?.websiteLink);
      setValue('facebookLink', BrandInfoState?.facebookLink);
      setValue('instagramLink', BrandInfoState?.instagramLink);
      setValue('other', BrandInfoState?.other);
      setValue('einNumber', BrandInfoState?.einNumber);
      setValue('tmCertificate', BrandInfoState?.tmCertificate);
      setValue('additionalDocument', BrandInfoState?.additionalDocument);
    }
  }, [userDetails, setValue, BrandInfoState]);

  if (!userDetails) return null;

  return (
    <>
      <HistoryRouting history='Program Management/Company Information' />
      {/* <PageShdowTitle title={'Company Information'} /> */}

      <form onSubmit={handleSubmit(onSubmit)}>
        {userDetails.admin && (
          <div className='my-6 text-right'>
            <button
              type='button'
              onClick={handleEditToggle}
              className='text-[#d50000] text-[1.25rem] pr-1 font-semibold underline'
            >
              {isEditing ? 'Cancel' : 'Edit'}
            </button>
          </div>
        )}
        <div className='rounded-b-lg primary-shadow'>
          <div className=''>
            <h2 className='text-white bg-primary p-4 px-8 rounded-t-lg text-lg font-semibold'>
              Company Information
            </h2>

            <div className='p-8'>
              <div className='mb-6'>
                <label className='block text-[#000000] text-sm font-[500] mb-2'>
                  Company Name <RequiredAsterisk isEditing={isEditing} />
                </label>
                <input
                  type='text'
                  className='register-input w-full'
                  {...register('companyName', {
                    required: 'Company name is required',
                  })}
                  disabled={!isEditing}
                />
                {errors.companyName && (
                  <span className='text-red-600 text-[0.75rem] font-semibold'>
                    {errors.companyName.message}
                  </span>
                )}
              </div>

              <div className='mb-6 mt-4 grid md:grid-cols-2 gap-x-8 gap-y-4'>
                <div>
                  <label className='block text-[#000000] text-sm font-[500] mb-2'>
                    Address Line 1 <RequiredAsterisk isEditing={isEditing} />
                  </label>
                  <input
                    type='text'
                    className='register-input w-full'
                    {...register('addressOne')}
                    disabled={!isEditing}
                  />
                  {errors.addressOne && (
                    <span className='text-red-600 text-[0.75rem] font-semibold'>
                      {errors.addressOne.message}
                    </span>
                  )}
                </div>
                <div>
                  <label className='block text-[#000000] text-sm font-[500] mb-2'>
                    Address Line 2
                  </label>
                  <input
                    type='text'
                    className='register-input w-full'
                    {...register('addressTwo')}
                    disabled={!isEditing}
                  />
                </div>
              </div>

              <div className='mb-6 mt-4 grid md:grid-cols-2 gap-x-8 gap-y-4'>
                <div>
                  <label className='block text-[#000000] text-sm font-[500] mb-2'>
                    City <RequiredAsterisk isEditing={isEditing} />
                  </label>
                  <input
                    type='text'
                    className='register-input w-full'
                    {...register('city')}
                    disabled={!isEditing}
                  />
                  {errors.city && (
                    <span className='text-red-600 text-[0.75rem] font-semibold'>
                      {errors.city.message}
                    </span>
                  )}
                </div>

                <div>
                  <label className='block text-[#000000] text-sm font-[500] mb-2'>
                    State <RequiredAsterisk isEditing={isEditing} />
                  </label>
                  <input
                    type='text'
                    className='register-input w-full'
                    {...register('state')}
                    disabled={!isEditing}
                  />
                  {errors.state && (
                    <span className='text-red-600 text-[0.75rem] font-semibold'>
                      {errors.state.message}
                    </span>
                  )}
                </div>
              </div>

              <div className='mb-6 mt-4 grid md:grid-cols-2 gap-x-8 gap-y-4'>
                <div>
                  <label className='block text-[#000000] text-sm font-[500] mb-2'>
                    Zip Code <RequiredAsterisk isEditing={isEditing} />
                  </label>
                  <input
                    type='text'
                    className='register-input w-full'
                    {...register('zipcode')}
                    disabled={!isEditing}
                  />
                  {errors.zipcode && (
                    <span className='text-red-600 text-[0.75rem] font-semibold'>
                      {errors.zipcode.message}
                    </span>
                  )}
                </div>

                <div>
                  <label className='block text-[#000000] text-sm font-[500] mb-2'>
                    Country <RequiredAsterisk isEditing={isEditing} />
                  </label>
                  <input
                    type='text'
                    className='register-input w-full'
                    {...register('country')}
                    disabled={!isEditing}
                  />
                  {errors.country && (
                    <span className='text-red-600 text-[0.75rem] font-semibold'>
                      {errors.country.message}
                    </span>
                  )}
                </div>
              </div>

              <div className='mb-6 mt-4 grid md:grid-cols-2 gap-x-8 gap-y-4'>
                <div>
                  <label className='block text-[#000000] text-sm font-[500] mb-2'>
                    Contact Email <RequiredAsterisk isEditing={isEditing} />
                  </label>
                  <input
                    type='email'
                    className='register-input w-full'
                    {...register('contactEmail', {
                      required: 'Contact email is required',
                      pattern: {
                        value: emailPattern,
                        message: 'Invalid email address',
                      },
                    })}
                    disabled={!isEditing}
                  />
                  {errors.contactEmail && (
                    <span className='text-red-600 text-[0.75rem] font-semibold'>
                      {errors.contactEmail.message}
                    </span>
                  )}
                </div>

                <div>
                  <label className='block text-[#000000] text-sm font-[500] mb-3'>
                    Company Phone <RequiredAsterisk isEditing={isEditing} />
                  </label>
                  <input
                    type='text'
                    className='register-input w-full'
                    {...register('companyPhone', {
                      required: 'Company phone is required',
                      pattern: {
                        value: phonePattern,
                        message: 'Invalid phone number',
                      },
                    })}
                    disabled={!isEditing}
                  />

                  {errors.companyPhone && (
                    <span className='text-red-600 text-[0.75rem] font-semibold'>
                      {errors.companyPhone.message}
                    </span>
                  )}
                </div>
              </div>

              <div className='mb-6 mt-4 grid md:grid-cols-2 gap-x-8 gap-y-4'>
                <div>
                  <label className='block text-[#000000] text-sm font-[500] mb-2'>
                    Sales Agent ID <RequiredAsterisk isEditing={isEditing} />
                  </label>
                  <input
                    type='number'
                    className='register-input w-full'
                    {...register('salesAgentId', {
                      required: 'Sales Agent ID is required',
                      pattern: {
                        value: salesIdPattern,
                        message: 'Invalid Sales Agent ID',
                      },
                    })}
                    disabled={!isEditing}
                    readOnly
                  />
                  {errors.salesAgentId && (
                    <span className='text-red-600 text-[0.75rem] font-semibold'>
                      {errors.salesAgentId.message}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
          <Toaster position='top-center' />
          <Modal isLoading={isLoading}>{isLoading && <Spinner />}</Modal>
        </div>
        <div className='my-6 text-right flex justify-end'>
          <button
            type='button'
            onClick={handleSecondEditToggle}
            className='text-[#d50000] text-[1.25rem] pr-1 font-semibold underline'
          >
            {isEditingSecond ? 'Cancel' : 'Edit'}
          </button>
        </div>
        <div
          className={`justify-center max-w-[full] mx-auto primary-shadow rounded-lg ${
            !isEditingSecond ? 'pointer-events-none' : ''
          }`}
        >
          <div className='bg-white primary-shadow rounded-lg w-full'>
            <div className='mb-8'>
              <div className='mb-6 pb-4'>
                <h2 className='text-white bg-primary p-4 px-8 rounded-t-lg text-lg font-semibold'>
                  Social Media Info
                </h2>
                <div className='mb-0 mt-4 px-4 md:px-8 md:py-4'>
                  <p className='text-base mb-8 text-[#878787]'>
                    Enter your organization&apos;s website and social media
                    links here.
                  </p>

                  <div className='grid md:grid-cols-2 gap-x-8 gap-y-4'>
                    <div>
                      <label className='block text-[#000000] text-sm font-[500] mb-2'>
                        Website <RequiredAsterisk isEditing={isEditingSecond} />
                      </label>
                      <input
                        type='text'
                        id='websiteLink'
                        className={`register-input w-full  ${
                          !isEditingSecond && 'read-only-input'
                        }`}
                        {...register('websiteLink', {
                          required: 'Website Link is required',
                        })}
                      />
                      <p className='text-[0.75rem] font-semibold text-[#d90000] mt-[5px]'>
                        {errors.websiteLink?.message}
                      </p>
                    </div>
                    <div>
                      <label className='block text-[#000000] text-sm font-[500] mb-2'>
                        Facebook
                      </label>
                      <input
                        type='text'
                        id='facebookLink'
                        className={`register-input w-full  ${
                          !isEditingSecond && 'read-only-input'
                        }`}
                        {...register('facebookLink')}
                      />
                      <p className='text-[0.75rem] font-semibold text-[#d90000] mt-[5px]'>
                        {errors.facebookLink?.message}
                      </p>
                    </div>
                  </div>
                </div>
                {/* Row 2 */}
                <div className='mb-0 mt-4 px-4 md:px-8 md:py-4'>
                  <div className='grid md:grid-cols-2 gap-x-8 gap-y-4'>
                    <div>
                      <label className='block text-[#000000] text-sm font-[500] mb-2'>
                        Instagram
                      </label>
                      <input
                        type='text'
                        id='instagramLink'
                        className={`register-input w-full  ${
                          !isEditingSecond && 'read-only-input'
                        }`}
                        {...register('instagramLink')}
                      />
                      <p className='text-[0.75rem] font-semibold text-[#d90000] mt-[5px]'>
                        {errors.instagramLink?.message}
                      </p>
                    </div>
                    <div>
                      <label className='block text-[#000000] text-sm font-[500] mb-2'>
                        Other
                      </label>
                      <input
                        type='text'
                        id='other'
                        className={`register-input w-full  ${
                          !isEditingSecond && 'read-only-input'
                        }`}
                        {...register('other')}
                      />
                      <p className='text-[0.75rem] font-semibold text-[#d90000] mt-[5px]'>
                        {errors.other?.message}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className='my-6 text-right flex justify-end'>
          <button
            type='button'
            onClick={handleThirdEditToggle}
            className='text-[#d50000] text-[1.25rem] pr-1 font-semibold underline'
          >
            {isEditingThird ? 'Cancel' : 'Edit'}
          </button>
        </div>

        <div
          className={`justify-center max-w-[full] mx-auto primary-shadow rounded-lg ${
            !isEditingThird ? 'pointer-events-none' : ''
          }`}
        >
          <div className='bg-white primary-shadow rounded-lg w-full'>
            <div className='mb-8'>
              <div className='mb-6  pb-4'>
                <h2 className='text-white bg-primary p-4 px-8 rounded-t-lg text-lg font-semibold'>
                  Additional Program Info
                </h2>
                <div className='mb-0 mt-4 px-4 md:px-8 md:py-4'>
                  <p className='text-base mb-8 text-[#878787] '>
                    Enter your organization&apos;s TIN/EIN (Taxpayer / Employer
                    Identification Number). Upload your organization&apos;s
                    Company Registration / Incorporation Certificate and
                    Trademark Certificate(s), if any.
                  </p>

                  {/* row 1 */}
                  <div className='grid md:grid-cols-2 gap-x-8 gap-y-4 md:mb-6 mb-4'>
                    <FileUpload
                      name='additionalDocument'
                      label='Company Registration Certificate'
                      isRequired={true}
                      requiredMessage='Company Registration Certificate is required'
                      isLoading={loadingBrandInfo}
                      register={register}
                      errors={errors}
                      watch={watch}
                      setError={setError}
                      clearErrors={clearErrors}
                      setValue={setValue}
                      backendFileName={
                        BrandInfoState?.additionalDocument
                          ? extractFileName(BrandInfoState?.additionalDocument)
                          : ''
                      }
                      maxFileSizeMB={2}
                    />

                    <div>
                      <label className='block text-[#000000] text-sm font-[500] mb-2'>
                        TIN/EIN Number
                        <RequiredAsterisk isEditing={isEditing} />
                      </label>
                      <input
                        type='text'
                        id='einNumber'
                        className={`register-input w-full  ${
                          !isEditingThird && 'read-only-input'
                        }`}
                        {...register('einNumber', {
                          required: 'TIN/EIN Number is required',
                        })}
                      />
                      <p className='text-[0.75rem] font-semibold text-[#d90000] mt-[5px]'>
                        {errors.einNumber?.message}
                      </p>
                    </div>
                  </div>

                  {/* Additional Info - row 2 */}
                  <div className='grid md:grid-cols-2 gap-x-8 gap-y-4'>
                    <FileUpload
                      name='tmCertificate'
                      label='Trademark Certificate'
                      register={register}
                      errors={errors}
                      isLoading={loadingBrandInfo}
                      watch={watch}
                      setError={setError}
                      clearErrors={clearErrors}
                      setValue={setValue}
                      backendFileName={
                        BrandInfoState?.tmCertificate
                          ? extractFileName(BrandInfoState?.tmCertificate)
                          : ''
                      }
                      maxFileSizeMB={2}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {(isEditing || isEditingSecond || isEditingThird) && (
          <div className='mt-8 w-full flex justify-end gap-4'>
            <Button
              type='submit'
              className={`w-full  text-sm px-5 text-white bg-primary border-primary min-w-[7rem] py-2 md:w-[7rem] rounded-[10px] font-semibold`}
            >
              Save Changes
            </Button>
          </div>
        )}
      </form>
    </>
  );
};

export default CompanyInfo;
